import { AppLayout } from "@/components/layout/AppLayout";
import { useFleetAlerts, FleetAlert, AlertStatus, AlertSeverity } from "@/hooks/useFleetAlerts";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  TrendingDown,
  DollarSign,
  Clock,
  Wrench,
  BarChart3,
  PauseCircle,
  ChevronDown,
  ChevronUp,
  Search,
  CheckCircle2,
  Bell,
  BellOff,
} from "lucide-react";
import { useState, useMemo } from "react";

const alertTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  expense_spike: DollarSign,
  profit_drop: TrendingDown,
  idle_vehicle: PauseCircle,
  cleaning_outlier: DollarSign,
  maintenance_spike: Wrench,
  high_expense_ratio: BarChart3,
  revenue_decline: TrendingDown,
};

const severityStyles: Record<AlertSeverity, string> = {
  high: "bg-red-500/10 text-red-600 border-red-500/20",
  medium: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  low: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

const severityBorder: Record<AlertSeverity, string> = {
  high: "border-l-red-500",
  medium: "border-l-amber-500",
  low: "border-l-emerald-500",
};

function formatTimestamp(d: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week(s) ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const AlertCard = ({ alert: initialAlert, onStatusChange }: { alert: FleetAlert; onStatusChange: (id: string, status: AlertStatus) => void }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setLocalStatus] = useState<AlertStatus>(initialAlert.status);
  const Icon = alertTypeIcons[initialAlert.type] || AlertTriangle;

  const handleStatusChange = (newStatus: AlertStatus) => {
    setLocalStatus(newStatus);
    onStatusChange(initialAlert.id, newStatus);
  };

  return (
    <Card className={`border-l-4 ${severityBorder[initialAlert.severity]} ${status !== "new" ? "opacity-60" : ""}`}>
      <CardContent className="p-5">
        {/* Header row */}
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-muted shrink-0 mt-0.5">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="text-xs">{initialAlert.vehicleName}</Badge>
                  <Badge variant="outline" className={`text-xs ${severityStyles[initialAlert.severity]}`}>
                    {initialAlert.severity.charAt(0).toUpperCase() + initialAlert.severity.slice(1)}
                  </Badge>
                  <Badge variant="outline" className="text-xs">{initialAlert.typeLabel}</Badge>
                </div>
                <p className="font-medium text-foreground mt-1.5 leading-snug">{initialAlert.description}</p>
              </div>
              <span className="text-xs text-muted-foreground shrink-0 mt-1">
                {formatTimestamp(initialAlert.timestamp)}
              </span>
            </div>

            <p className="text-sm text-muted-foreground mt-1.5">{initialAlert.whyTriggered}</p>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button variant="ghost" size="sm" className="text-xs h-8" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
                {expanded ? "Less" : "Investigate"}
              </Button>
              {status === "new" && (
                <Button variant="ghost" size="sm" className="text-xs h-8 text-muted-foreground" onClick={() => handleStatusChange("acknowledged")}>
                  <Bell className="h-3.5 w-3.5 mr-1" />
                  Acknowledge
                </Button>
              )}
              {status !== "resolved" && (
                <Button variant="ghost" size="sm" className="text-xs h-8 text-muted-foreground" onClick={() => handleStatusChange("resolved")}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  Resolve
                </Button>
              )}
            </div>

            {expanded && (
              <div className="mt-4 border-t pt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Baseline</p>
                    <div className="space-y-1">
                      {initialAlert.details.baseline.map((m) => (
                        <div key={m.label} className="text-sm">
                          <span className="text-muted-foreground">{m.label}: </span>
                          <span className="font-medium text-foreground">{m.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Current</p>
                    <div className="space-y-1">
                      {initialAlert.details.comparison.map((m) => (
                        <div key={m.label} className="text-sm">
                          <span className="text-muted-foreground">{m.label}: </span>
                          <span className="font-medium text-foreground">{m.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AlertsExceptions = () => {
  const { alerts, vehicleData, isLoading } = useFleetAlerts();
  const [activeTab, setActiveTab] = useState<AlertStatus>("new");

  // Track local status overrides
  const [statusOverrides, setStatusOverrides] = useState<Record<string, AlertStatus>>({});

  const getAlertStatus = (alert: FleetAlert): AlertStatus => {
    return statusOverrides[alert.id] || alert.status;
  };

  const filteredAlerts = useMemo(() => {
    return alerts.filter((a) => getAlertStatus(a) === activeTab);
  }, [alerts, activeTab, statusOverrides]);

  const counts = useMemo(() => {
    const c = { new: 0, acknowledged: 0, resolved: 0 };
    for (const a of alerts) {
      c[getAlertStatus(a)]++;
    }
    return c;
  }, [alerts, statusOverrides]);

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Alerts & Exceptions</h1>
          <p className="text-muted-foreground mt-1">Unusual activity across your fleet.</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as AlertStatus)}>
          <TabsList>
            <TabsTrigger value="new">
              New {counts.new > 0 && <Badge variant="secondary" className="ml-1.5 text-xs h-5 min-w-5 px-1.5">{counts.new}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="acknowledged">
              Acknowledged {counts.acknowledged > 0 && <Badge variant="secondary" className="ml-1.5 text-xs h-5 min-w-5 px-1.5">{counts.acknowledged}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="resolved">
              Resolved
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        ) : vehicleData.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Bell className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">Need fleet data</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add vehicles and import trips and expenses to see alerts.
              </p>
            </CardContent>
          </Card>
        ) : filteredAlerts.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              {activeTab === "new" ? (
                <>
                  <BellOff className="h-10 w-10 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground">No new alerts</h3>
                  <p className="text-sm text-muted-foreground mt-1">No unusual activity detected.</p>
                </>
              ) : activeTab === "acknowledged" ? (
                <>
                  <Bell className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground">No acknowledged alerts</h3>
                  <p className="text-sm text-muted-foreground mt-1">Alerts you acknowledge will appear here.</p>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground">No resolved alerts</h3>
                  <p className="text-sm text-muted-foreground mt-1">Resolved alerts will appear here for reference.</p>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredAlerts.map((alert) => (
              <AlertCard key={alert.id} alert={alert} onStatusChange={(id, s) => setStatusOverrides(prev => ({ ...prev, [id]: s }))} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default AlertsExceptions;
